
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import { useEffect } from "react";

interface SEOProps {
  title: string;
  description: string;
  canonicalUrl?: string;
  schema?: object;
  ogImage?: string;
}

export const SEO = ({ 
  title, 
  description, 
  canonicalUrl, 
  schema,
  ogImage = "/lovable-uploads/398bc7b6-8703-40ce-9eac-01abc8146c20.png"
}: SEOProps) => {
  const location = useLocation();
  
  console.log('SEO Component rendering with:', {
    title,
    description,
    canonicalUrl,
    currentPath: location.pathname,
    schema
  });

  // Get canonical hostname - always use www version
  const getCanonicalHostname = () => {
    const origin = window.location.origin;
    // If already www, return as is. Otherwise, add www
    if (origin.includes('www.')) {
      return origin;
    }
    // Replace homeprosokc.com with www.homeprosokc.com
    return origin.replace('homeprosokc.com', 'www.homeprosokc.com');
  };
  const currentHostname = getCanonicalHostname();
  
  // If no canonical URL is provided, use the current path
  const fullCanonicalUrl = canonicalUrl 
    ? `${currentHostname}${canonicalUrl.startsWith('/') ? canonicalUrl : `/${canonicalUrl}`}` 
    : `${currentHostname}${location.pathname}`;

  // Remove any query parameters from canonical URL
  const cleanCanonicalUrl = fullCanonicalUrl.split('?')[0];
  
  // Remove trailing slashes from canonical URL
  const finalCanonicalUrl = cleanCanonicalUrl.replace(/\/$/, '');

  // Get the complete current URL for sharing (including any query params)
  const shareUrl = window.location.href;
  console.log('Share URL being set:', shareUrl);

  // Default schema if none provided
  const defaultSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "url": currentHostname,
    "logo": `${currentHostname}/lovable-uploads/398bc7b6-8703-40ce-9eac-01abc8146c20.png`,
    "name": "Home Pros OKC",
    "image": {
      "@type": "ImageObject",
      "url": `${currentHostname}/lovable-uploads/398bc7b6-8703-40ce-9eac-01abc8146c20.png`,
      "width": "500",
      "height": "500",
      "caption": "Home Pros OKC - Connecting Oklahoma City homeowners with trusted local service professionals",
      "alternateName": "Home Pros OKC Logo"
    }
  };

  // Handle relative or absolute image paths for OG image
  const fullOgImageUrl = ogImage?.startsWith('http') 
    ? ogImage 
    : `${currentHostname}${ogImage.startsWith('/') ? ogImage : `/${ogImage}`}`;

  // Add a hook to dynamically update the sharing URL meta tag
  useEffect(() => {
    // iOS specific approach - dynamically add a direct link element for iOS
    const existingLinkForIOS = document.querySelector('link[rel="canonical"][data-ios-share="true"]');
    if (existingLinkForIOS) {
      existingLinkForIOS.remove();
    }
    
    const linkForIOS = document.createElement('link');
    linkForIOS.setAttribute('rel', 'canonical');
    linkForIOS.setAttribute('href', window.location.href);
    linkForIOS.setAttribute('data-ios-share', 'true');
    document.head.appendChild(linkForIOS);

    return () => {
      const linkElement = document.querySelector('link[rel="canonical"][data-ios-share="true"]');
      if (linkElement) {
        linkElement.remove();
      }
    };
  }, [location.pathname, location.search]);

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={finalCanonicalUrl} />
      
      {/* Open Graph Meta Tags */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={shareUrl} />
      <meta property="og:image" content={fullOgImageUrl} />
      <meta property="og:image:width" content="500" />
      <meta property="og:image:height" content="500" />
      <meta property="og:image:alt" content="Modern house roofline logo representing Home Pros OKC's commitment to quality home services in Oklahoma City" />
      <meta property="og:site_name" content="Home Pros OKC" />
      
      {/* Twitter Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullOgImageUrl} />
      <meta name="twitter:image:alt" content="Modern house roofline logo representing Home Pros OKC's commitment to quality home services in Oklahoma City" />
      
      {/* Schema.org JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(schema || defaultSchema)}
      </script>
    </Helmet>
  );
};
